import Client from 'models/Client';
import type { IntegrationType } from 'services/api/fields/admin/client';

/**
 * Display names for each integration type, in priority order.
 *
 * The declaration order is meaningful — when a client has more than one
 * integration configured (e.g. an EHR + the Kno2 overlay), the first entry
 * here that the client has wins. Source EHRs come before Kno2 because the
 * CCD content originates in the source EHR.
 *
 * `hospitaldischarge` is LAST for the same reason Kno2 is late: it is a
 * capability a client carries alongside an EHR, not an EHR itself, and an
 * Epic client carries both (OLIO-3517). Placing it any earlier would make
 * `primaryIntegrationType` return it in preference to the real source EHR —
 * the same list-order trap that had to be fixed on the platform side, where
 * two readers picked a client's EMR with `[0]` after filtering only Kno2.
 */
const INTEGRATION_DISPLAY_NAMES: Record<IntegrationType, string> = {
  matrix: 'MatrixCare',
  pcc: 'PointClickCare',
  epic: 'Epic',
  kno2: 'Kno2',
  hospitaldischarge: 'Hospital Discharge',
};

/**
 * True if the client has at least one EHR integration configured.
 */
export function clientHasIntegration(client?: Client | null): boolean {
  return !!client && client.integrationTypes.length > 0;
}

/**
 * The client's primary integration type, following the priority order in
 * `INTEGRATION_DISPLAY_NAMES`. Returns undefined when the client has no
 * integration configured.
 */
export function primaryIntegrationType(client?: Client | null): IntegrationType | undefined {
  if (!client || client.integrationTypes.length === 0) return undefined;
  return (Object.keys(INTEGRATION_DISPLAY_NAMES) as IntegrationType[]).find((t) => client.integrationTypes.includes(t));
}

/**
 * Render-ready display name for the client's primary EHR integration.
 */
export function integrationDisplayName(client?: Client | null): string {
  const primary = primaryIntegrationType(client);
  return primary ? INTEGRATION_DISPLAY_NAMES[primary] : '';
}
